import type { SupabaseClient } from '@supabase/supabase-js';
import { derivarStatusBoleto, valorDevido } from './statusBoleto.ts';
import type { DadosStatusBoleto } from './statusBoleto.ts';
import { palavrasDoNome } from './nomePagador.ts';
import type { TrocaDonoUc } from './trocaDonoUc.ts';

/**
 * Titulares da UC — quem ocupou cada unidade, de quando até quando e por quê
 * (#6238, migration 20261012120000).
 *
 * A UC é o "chassi": continua sendo um cadastro só. Nome, CPF e contatos nas
 * colunas dela são "o atual"; a tabela `uc_titulares` guarda um período por
 * pessoa, gravado pelo gatilho da UC. São dois papéis, que mudam em datas
 * diferentes: o cliente da Vigor (quem assina e paga) e o titular da conta de luz.
 *
 * "De quem é o débito" não tem coluna própria: vale o pagador gravado no boleto
 * e o carimbo da troca de dono. `atribuirFaturas` casa os dois com os períodos.
 *
 * Sem JSX, roda sob `node --test`.
 */

export type PapelTitular = 'cliente' | 'titular_conta';

export type MotivoTitular =
  | 'carga_inicial'
  | 'cadastro'
  | 'edicao_cadastro'
  | 'troca_dono'
  | 'aditivo'
  | 'registro_manual';

export interface TitularUc {
  id: string;
  uc_id: number;
  numero_uc: string | null;
  papel: PapelTitular;
  nome: string | null;
  /** Só dígitos; nulo quando o cadastro não tinha documento válido. */
  cpf_cnpj: string | null;
  contatos: { whatsapp?: string; email?: string };
  negociacao_id: number | null;
  inicio: string;
  /** A data de início não é conhecida: a carga inicial usou a data do cadastro da UC. */
  inicio_estimado: boolean;
  /** Nulo = é o atual. */
  fim: string | null;
  motivo: MotivoTitular;
  motivo_fim: string | null;
  origem_tabela: string | null;
  origem_id: string | null;
  observacao: string | null;
  registrado_por: string | null;
  registrado_por_nome: string | null;
  created_at: string;
}

export const ROTULO_PAPEL: Record<PapelTitular, string> = {
  cliente: 'Cliente da Vigor',
  titular_conta: 'Titular da conta de luz',
};

// ─── Comparação de pessoas ───────────────────────────────────────────────────
//
// MESMA regra de `_uc_titular_mesma_pessoa` no banco — mudou aqui, muda lá.

export interface Pessoa {
  nome?: string | null;
  documento?: string | null;
}

/** CPF/CNPJ só com dígitos; `null` quando não tem 11 nem 14 (vazio, mascarado, pela metade). */
export const documentoValido = (v: string | null | undefined): string | null => {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length === 11 || d.length === 14 ? d : null;
};

function distancia(a: string, b: string): number {
  if (a === b) return 0;
  const ant = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diag = ant[0];
    ant[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = ant[j];
      ant[j] = Math.min(ant[j] + 1, ant[j - 1] + 1, diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return ant[b.length];
}

/** Mesma palavra: igual, cortada ("SAUR" de "SAURO") ou com erro de digitação. */
function palavraParecida(a: string, b: string): boolean {
  if (a === b) return true;
  const menor = Math.min(a.length, b.length);
  if (menor >= 4 && (a.startsWith(b) || b.startsWith(a))) return true;
  return menor >= 5 && distancia(a, b) <= (menor >= 8 ? 2 : 1);
}

/**
 * É a mesma pessoa?
 * - os dois lados com documento: o documento decide. Documento diferente só é a
 *   mesma pessoa se o nome for o mesmo (CPF corrigido, ou o CNPJ de "JOAO SILVA ME");
 * - falta documento de um lado: vale o nome — todas as palavras do nome mais
 *   curto aparecem no outro.
 */
export function mesmaPessoa(a: Pessoa, b: Pessoa): boolean {
  const da = documentoValido(a.documento);
  const db = documentoValido(b.documento);
  const pa = palavrasDoNome(a.nome);
  const pb = palavrasDoNome(b.nome);
  if (da && db) {
    if (da === db) return true;
    return pa.length > 0 && [...pa].sort().join(' ') === [...pb].sort().join(' ');
  }
  // Sem nome de um lado não há como dizer que é outra pessoa.
  if (pa.length === 0 || pb.length === 0) return true;
  const [curto, longo] = pa.length <= pb.length ? [pa, pb] : [pb, pa];
  const achou = curto.filter(p => longo.some(q => palavraParecida(p, q))).length;
  // Um prenome solto ("MARIA") só casa com o começo do outro nome.
  return achou === curto.length && (curto.length >= 2 || palavraParecida(curto[0], longo[0]));
}

// ─── Textos ──────────────────────────────────────────────────────────────────

const FUSO = 'America/Sao_Paulo';
export const formatarDia = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: FUSO }) : '';

export const ehAtual = (t: Pick<TitularUc, 'fim'>): boolean => !t.fim;

/** "desde 10/01/2025" ou "de 10/01/2025 a 05/09/2026". */
export const descreverPeriodo = (t: Pick<TitularUc, 'inicio' | 'fim'>): string =>
  t.fim ? `de ${formatarDia(t.inicio)} a ${formatarDia(t.fim)}` : `desde ${formatarDia(t.inicio)}`;

/** Por que o período começou, em frase curta. */
export function descreverMotivo(t: Pick<TitularUc, 'motivo' | 'observacao' | 'registrado_por_nome'>): string {
  switch (t.motivo) {
    case 'carga_inicial':
      return 'Já estava no cadastro quando este histórico começou a ser guardado';
    case 'cadastro':
      return 'Cadastro da UC';
    case 'edicao_cadastro':
      return 'Cadastro da UC alterado';
    case 'troca_dono':
      return 'Troca de dono (venda do estabelecimento)';
    case 'aditivo':
      return t.observacao?.trim() || 'Aditivo contratual';
    case 'registro_manual':
      return `Registrado à mão${t.registrado_por_nome ? ` por ${t.registrado_por_nome}` : ''}`;
  }
}

/** Atual primeiro, depois do mais recente para o mais antigo. */
export const ordenarTitulares = <T extends Pick<TitularUc, 'inicio' | 'fim'>>(lista: T[]): T[] =>
  [...lista].sort((a, b) => {
    if (!a.fim !== !b.fim) return a.fim ? 1 : -1;
    return new Date(b.inicio).getTime() - new Date(a.inicio).getTime();
  });

// ─── De quem é cada fatura ───────────────────────────────────────────────────

export interface FaturaHistorico extends DadosStatusBoleto {
  id: number | string;
  mes_referencia?: string | null;
  created_at?: string | null;
  emitido_em?: string | null;
  nome_cliente_boleto?: string | null;
  cpf_cnpj_cliente_boleto?: string | null;
  dono_anterior_troca_id?: string | null;
}

export interface ResumoCobranca {
  /** Faturas que viraram cobrança (pagas, em aberto, vencidas ou parceladas). */
  cobrancas: number;
  pagas: number;
  parceladas: number;
  emAberto: number;
  vencidas: number;
  /** A vencer + vencido: o que ainda falta receber. */
  valorEmAberto: number;
  valorVencido: number;
}

export function resumirCobrancas(faturas: DadosStatusBoleto[], hoje: Date = new Date()): ResumoCobranca {
  const r: ResumoCobranca = { cobrancas: 0, pagas: 0, parceladas: 0, emAberto: 0, vencidas: 0, valorEmAberto: 0, valorVencido: 0 };
  for (const f of faturas) {
    const status = derivarStatusBoleto(f, hoje);
    if (status === 'Pago' || status === 'Quitado') { r.cobrancas++; r.pagas++; }
    else if (status === 'Parcelado') { r.cobrancas++; r.parceladas++; }
    else if (status === 'Em aberto' || status === 'Vencido') {
      r.cobrancas++;
      r.emAberto++;
      r.valorEmAberto += valorDevido(f);
      if (status === 'Vencido') { r.vencidas++; r.valorVencido += valorDevido(f); }
    }
  }
  r.valorEmAberto = Math.round(r.valorEmAberto * 100) / 100;
  r.valorVencido = Math.round(r.valorVencido * 100) / 100;
  return r;
}

export interface GrupoFaturas {
  /** id do período, ou `pagador:<chave>` quando o boleto saiu no nome de quem não consta como titular. */
  chave: string;
  titular: TitularUc | null;
  /** Preenchido quando `titular` é nulo: o nome que está nos boletos. */
  pagador: { nome: string; documento: string | null } | null;
  faturas: FaturaHistorico[];
  resumo: ResumoCobranca;
}

const tempo = (iso: string | null | undefined): number => (iso ? new Date(iso).getTime() : NaN);

/** O período em vigor na data; antes do primeiro vale o primeiro, depois do último vale o último. */
function periodoNaData(periodos: TitularUc[], quando: number): TitularUc | null {
  if (periodos.length === 0) return null;
  if (Number.isNaN(quando)) return periodos[periodos.length - 1];
  const dentro = periodos.find(p => tempo(p.inicio) <= quando && (!p.fim || quando < tempo(p.fim)));
  if (dentro) return dentro;
  return quando < tempo(periodos[0].inicio) ? periodos[0] : periodos[periodos.length - 1];
}

/**
 * Separa as faturas da UC por quem era o cliente.
 *
 * 1. Fatura carimbada na troca de dono (`dono_anterior_troca_id`): é de quem
 *    saiu naquela troca.
 * 2. Boleto com pagador gravado: é de quem está no boleto. Se o pagador não é
 *    nenhum dos titulares registrados, fica num grupo próprio, com o nome dele —
 *    é a pista de um titular antigo que o sistema não registrou, ou de um boleto
 *    no nome de outra pessoa (o assinante da negociação, por exemplo).
 * 3. Sem pagador (fatura sem boleto): pela data.
 *
 * Devolve um grupo por período de cliente (mesmo sem fatura), o atual primeiro,
 * e depois os grupos de pagador sem titular.
 */
export function atribuirFaturas(
  faturas: FaturaHistorico[],
  titulares: TitularUc[],
  trocas: Pick<TrocaDonoUc, 'id' | 'dono_anterior'>[] = [],
  hoje: Date = new Date(),
): GrupoFaturas[] {
  const clientes = titulares
    .filter(t => t.papel === 'cliente')
    .sort((a, b) => tempo(a.inicio) - tempo(b.inicio));
  const porTitular = new Map<string, FaturaHistorico[]>(clientes.map(c => [c.id, []]));
  const avulsos: { pessoa: Pessoa; faturas: FaturaHistorico[] }[] = [];

  const paraAvulso = (pessoa: Pessoa, f: FaturaHistorico) => {
    const grupo = avulsos.find(g => {
      const da = documentoValido(g.pessoa.documento);
      const db = documentoValido(pessoa.documento);
      // Sem nome dos dois lados, só o documento separa — `mesmaPessoa` diria "não sei".
      if (palavrasDoNome(g.pessoa.nome).length === 0 || palavrasDoNome(pessoa.nome).length === 0) return !!da && da === db;
      return mesmaPessoa(g.pessoa, pessoa);
    });
    if (grupo) grupo.faturas.push(f);
    else avulsos.push({ pessoa, faturas: [f] });
  };

  for (const f of faturas) {
    const quando = tempo(f.emitido_em) || tempo(f.created_at);

    if (f.dono_anterior_troca_id) {
      const idx = clientes.findIndex(c => c.origem_tabela === 'uc_trocas_dono' && c.origem_id === f.dono_anterior_troca_id);
      if (idx > 0) { porTitular.get(clientes[idx - 1].id)!.push(f); continue; }
      // Troca anterior a esta tabela: sobra o cadastro guardado no pedido.
      const d = trocas.find(t => t.id === f.dono_anterior_troca_id)?.dono_anterior;
      const antigo: Pessoa = { nome: d?.nome_cliente, documento: d?.cpf_cnpj_cliente };
      const tem = palavrasDoNome(antigo.nome).length > 0 || !!documentoValido(antigo.documento);
      const casado = tem ? clientes.filter(c => mesmaPessoa({ nome: c.nome, documento: c.cpf_cnpj }, antigo)) : [];
      if (casado.length > 0) { porTitular.get(casado[casado.length - 1].id)!.push(f); continue; }
      paraAvulso(tem ? antigo : { nome: 'Dono anterior' }, f);
      continue;
    }

    const pagador: Pessoa = { nome: f.nome_cliente_boleto, documento: f.cpf_cnpj_cliente_boleto };
    const temPagador = palavrasDoNome(pagador.nome).length > 0 || !!documentoValido(pagador.documento);
    if (temPagador) {
      const casados = clientes.filter(c => mesmaPessoa({ nome: c.nome, documento: c.cpf_cnpj }, pagador));
      if (casados.length === 0) { paraAvulso(pagador, f); continue; }
      // A mesma pessoa saiu e voltou: decide a data.
      const escolhido = casados.length === 1 ? casados[0] : periodoNaData(casados, quando)!;
      porTitular.get(escolhido.id)!.push(f);
      continue;
    }

    const pelaData = periodoNaData(clientes, quando);
    if (pelaData) porTitular.get(pelaData.id)!.push(f);
    else paraAvulso({ nome: 'Sem titular registrado' }, f);
  }

  const grupos: GrupoFaturas[] = ordenarTitulares(clientes).map(c => ({
    chave: c.id,
    titular: c,
    pagador: null,
    faturas: porTitular.get(c.id)!,
    resumo: resumirCobrancas(porTitular.get(c.id)!, hoje),
  }));
  for (const g of avulsos) {
    const doc = documentoValido(g.pessoa.documento);
    const nome = (g.faturas.map(f => f.nome_cliente_boleto?.trim()).find(Boolean) || g.pessoa.nome || 'Sem nome').trim();
    grupos.push({
      chave: `pagador:${doc ?? palavrasDoNome(nome).join(' ')}`,
      titular: null,
      pagador: { nome, documento: doc },
      faturas: g.faturas,
      resumo: resumirCobrancas(g.faturas, hoje),
    });
  }
  return grupos;
}

/** Primeira e última data das faturas de um grupo — sugere o período de um titular não registrado. */
export function intervaloDasFaturas(faturas: FaturaHistorico[]): { inicio: string; fim: string } | null {
  const datas = faturas.map(f => tempo(f.emitido_em) || tempo(f.created_at)).filter(n => !Number.isNaN(n));
  if (datas.length === 0) return null;
  const dia = (n: number) => new Date(n).toLocaleDateString('en-CA', { timeZone: FUSO });
  return { inicio: dia(Math.min(...datas)), fim: dia(Math.max(...datas)) };
}

// ─── Registro manual de titular anterior ─────────────────────────────────────

export const JUSTIFICATIVA_MINIMA_TITULAR = 10;

export interface TitularAnteriorForm {
  nome: string;
  documento: string;
  /** AAAA-MM-DD */
  inicio: string;
  fim: string;
  justificativa: string;
}

/** Mensagem de erro do formulário, ou `null` se está bom. Mesmas regras do banco. */
export function validarTitularAnterior(f: TitularAnteriorForm, hoje: Date = new Date()): string | null {
  if (f.nome.trim().length < 3) return 'Informe o nome do titular anterior.';
  if (f.documento.trim() && !documentoValido(f.documento)) {
    return 'O CPF/CNPJ informado não tem 11 nem 14 números. Corrija ou deixe em branco.';
  }
  if (!f.inicio || !f.fim) return 'Informe de quando até quando a UC foi dessa pessoa.';
  if (f.fim <= f.inicio) return 'A data final precisa ser depois da inicial.';
  if (f.fim > hoje.toLocaleDateString('en-CA', { timeZone: FUSO })) {
    return 'A data final não pode ser no futuro: titular anterior é quem já saiu.';
  }
  if (f.justificativa.trim().length < JUSTIFICATIVA_MINIMA_TITULAR) {
    return `Escreva como você sabe disso (pelo menos ${JUSTIFICATIVA_MINIMA_TITULAR} letras). Ex.: "Contrato de locação de 2024 no nome dele".`;
  }
  return null;
}

// ─── Acesso ao banco ─────────────────────────────────────────────────────────

type Cliente = Pick<SupabaseClient, 'from' | 'rpc'>;

const COLUNAS =
  'id, uc_id, numero_uc, papel, nome, cpf_cnpj, contatos, negociacao_id, inicio, inicio_estimado, fim, motivo, motivo_fim, origem_tabela, origem_id, observacao, registrado_por, registrado_por_nome, created_at';

/** Colunas de `faturas` que `atribuirFaturas` e `resumirCobrancas` precisam. */
export const COLUNAS_FATURA_HISTORICO =
  'id, mes_referencia, created_at, emitido_em, nome_cliente_boleto, cpf_cnpj_cliente_boleto, dono_anterior_troca_id, status_fatura, status_boleto_vigor, valor_boleto_vigor, valor_emissao_boleto_unico, vencimento_boleto, data_vencimento, data_pgto_boleto, quitado_em, energia_injetada_gd1_kwh, energia_injetada_gd2_kwh, cobranca_agrupada';

/** Todos os períodos da UC, dos dois papéis. */
export async function buscarTitularesDaUc(client: Cliente, ucId: number): Promise<TitularUc[]> {
  const { data, error } = await client
    .from('uc_titulares')
    .select(COLUNAS)
    .eq('uc_id', ucId)
    .order('inicio', { ascending: false });
  if (error) throw error;
  return (data ?? []) as TitularUc[];
}

export async function registrarTitularAnterior(
  client: Cliente,
  ucId: number,
  f: TitularAnteriorForm,
  papel: PapelTitular = 'cliente',
): Promise<string> {
  const { data, error } = await client.rpc('registrar_titular_anterior_uc', {
    p_uc_id: ucId,
    p_nome: f.nome.trim(),
    p_cpf_cnpj: f.documento.trim() || null,
    p_inicio: f.inicio,
    p_fim: f.fim,
    p_justificativa: f.justificativa.trim(),
    p_papel: papel,
  });
  if (error) throw new Error(error.message || 'Não foi possível registrar o titular anterior.');
  return data as string;
}

export async function removerTitularAnterior(client: Cliente, titularId: string): Promise<void> {
  const { error } = await client.rpc('remover_titular_anterior_uc', { p_titular_id: titularId });
  if (error) throw new Error(error.message || 'Não foi possível remover o registro.');
}

export interface ResultadoTitularAditivo {
  trocou: boolean;
  troca_id?: string | null;
  titular_anterior?: string | null;
  faturas_separadas?: number;
  acordos_separados?: number;
  negociacao_id?: number | null;
  /** A negociação da UC continua no nome de outra pessoa: o boleto sai no nome do assinante dela. */
  assinante_negociacao?: string | null;
}

/** Vincula a UC ao titular do aditivo contratual (ver `aplicar_titular_aditivo_uc`). */
export async function aplicarTitularAditivo(
  client: Cliente,
  p: { ucId: number; nome: string; documento: string; email?: string | null; whatsapp?: string | null; numeroTermo?: string | null },
): Promise<ResultadoTitularAditivo> {
  const { data, error } = await client.rpc('aplicar_titular_aditivo_uc', {
    p_uc_id: p.ucId,
    p_nome: p.nome,
    p_cpf_cnpj: p.documento,
    p_email: p.email ?? null,
    p_whatsapp: p.whatsapp ?? null,
    p_numero_termo: p.numeroTermo ?? null,
  });
  if (error) throw new Error(error.message || 'Não foi possível vincular a UC ao titular do aditivo.');
  return (data ?? { trocou: false }) as ResultadoTitularAditivo;
}
